import {
	AspectRatioContainer,
	Container,
	CTA,
	Image,
	Richtext,
	Text,
	TextAlignment,
	TokenTextAppearance,
	ThemeProvider,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import styled from 'styled-components';
import { Media } from '@volkswagen-onehub/icons-core';
import { COLOR_TOKEN, isNewBrandDesign } from '../../../helpers/color-tokens';
import { useNovedadesCardClick } from '../hooks/use-novedades-card-click';
import { useNovedadesLayer } from '../hooks/use-novedades-layer';
import { NovedadesItem } from '@esportal/portal-service';
import { IContextNovedades } from '../models/context-novedades';

const CardContainer = styled.div`
	background: ${COLOR_TOKEN.background.card.primary};
	border-radius: 16px;
	height: 100%;
	padding: 24px;
	backdrop-filter: blur(10px);

	/* Shadow02 */
	box-shadow: ${isNewBrandDesign()
		? 'none'
		: '0px 2px 4px 0px rgba(0, 14, 38, 0.2), 0px 3px 15px 0px rgba(0, 14, 38, 0.1)'};

	/* Mobile & Tablet */
	@media (max-width: 959px) {
		padding-top: var(--size-dynamic0150);
		padding-bottom: var(--size-dynamic0150);
		padding-right: var(--size-dynamic0200);
		padding-left: var(--size-dynamic0200);
	}
`;

const NovedadesCardContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	gap: var(--size-dynamic0050);
`;

// text container ocupa todo el espacio del medio para poder
// alinear el cta en el margen inferior izquierdo
const NovedadesTextContainer = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const NovedadesImageWrapper = styled.div<{ hasVideo: boolean }>`
	position: relative;
	border-radius: 16px;
	overflow: hidden;
	${(props) =>
		props.hasVideo &&
		`&::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.2);
    }`}
`;

const NovedadesImage = styled(Image)`
	border-radius: 16px;
	height: 100%;
`;

const ImageWithCTA = styled.div`
	position: relative;
	display: flex;
`;

const VideoPlayerCTA = styled(CTA)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	height: 100%;
	width: 100%;

	& svg {
		height: 30%;
		width: 30%;
	}
`;

interface NovedadesCardProps {
	novedadesItem: NovedadesItem;
	context: React.Context<IContextNovedades>;
}

export const NovedadesCard = ({ novedadesItem, context }: NovedadesCardProps) => {
	if (!novedadesItem.card || !novedadesItem.layer) return; // provisional - para que no pete si recibe datos con el modelo antiguo

	const handleOnClick = useNovedadesCardClick({ item: novedadesItem, context });
	const { handleLayerOpen } = useNovedadesLayer({ item: novedadesItem, context });

	return (
		<CardContainer data-testid="novedades-card">
			<NovedadesCardContainer>
				<ImageWithCTA>
					<AspectRatioContainer ratio="16:9">
						<NovedadesImageWrapper hasVideo={!!novedadesItem.layer.videoUrl}>
							<NovedadesImage src={novedadesItem.card.imageUrl} alt={novedadesItem.card.title ?? ''} />
						</NovedadesImageWrapper>
					</AspectRatioContainer>

					{novedadesItem.layer.videoUrl && (
						<ThemeProvider theme="inverted">
							<VideoPlayerCTA tag="button" emphasis="tertiary" onClick={handleLayerOpen} icon={<Media />} />
						</ThemeProvider>
					)}
				</ImageWithCTA>

				<NovedadesTextContainer>
					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.left} bold>
						{novedadesItem.card.title}
					</Text>
					<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.left}>
						<Richtext markdown={novedadesItem.card.description ?? ''} />
					</Text>
				</NovedadesTextContainer>

				{(novedadesItem.card.cta.src || novedadesItem.isLayer) && (
					<Container horizontalAlign="flex-start">
						<CTA
							tag="button"
							emphasis="nbab"
							onClick={() => {
								novedadesItem.isLayer ? handleLayerOpen() : handleOnClick();
							}}
						>
							{novedadesItem.card.cta.label ?? 'Descubre más'}
						</CTA>
					</Container>
				)}
			</NovedadesCardContainer>
		</CardContainer>
	);
};
